// TemplateEditorDrawer.tsx
import * as React from "react";
import {
  Box,
  Stack,
  Typography,
  Drawer,
  IconButton,
  Button,
  TextField,
  Divider,
  MenuItem,
  Autocomplete,
  Tooltip,
  InputAdornment,
  Chip,
  LinearProgress,
  Snackbar,
  Alert,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import TimerIcon from "@mui/icons-material/Timer";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import {
  Exercise,
  ExerciseTemplate,
  SetInfo,
  TemplateType,
} from "../../../../models/types";

/* ---------- Types ---------- */
export type TemplateEditorDrawerProps = {
  open: boolean;
  anchor?: "left" | "right";
  draft?: TemplateType;
  exercises: Exercise[];
  onSave: (template: TemplateType) => Promise<void> | void;
  onDelete?: (templateId: string) => Promise<void> | void;
  onClose: () => void;
  onChange?: (draft: TemplateType) => void;
  darkMode?: Boolean | undefined;
  titleOverride?: string;
  filterExerciseOptions?: (
    options: Exercise[],
    state: { inputValue: string }
  ) => Exercise[];
  availableTags?: string[];
};

/* ---------- Helpers ---------- */
const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

const emptySet = (): SetInfo => ({
  id: uid(),
  previous: "—",
  current: { weight: 0, reps: 0 },
});

const cloneTemplate = (t: TemplateType): TemplateType =>
  JSON.parse(JSON.stringify(t));

const summarizeTemplate = (t: TemplateType) => {
  const totalSets = t.exercises.reduce((s, e) => s + (e.sets?.length || 0), 0);
  const totalExercises = t.exercises.length;
  return { totalSets, totalExercises };
};

const ensureHttp = (u?: string | null) => {
  const v = (u ?? "").trim();
  if (!v) return "";
  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
};

const openUrl = (u?: string | null) => {
  const href = ensureHttp(u);
  if (!href) return;
  window.open(href, "_blank", "noopener,noreferrer");
};

const exerciseSummaryLine = (et: ExerciseTemplate) => {
  const name = et.exercise?.name?.trim() || "Untitled exercise";
  const sets = et.sets || [];
  const firstTwo =
    sets.length > 0
      ? sets
        .slice(0, 2)
        .map(
          (s, i) =>
            `S${i + 1}: ${s.current.weight || 0}×${s.current.reps || 0}`
        )
        .join(" • ")
      : "no sets";
  const rest = `${et.restTimer?.valueInSeconds ?? 60}s rest`;
  return `${name} — ${sets.length} sets — ${firstTwo} — ${rest}`;
};

const setSummaryText = (s: SetInfo, index: number) => {
  const w = s.current?.weight ?? 0;
  const r = s.current?.reps ?? 0;
  const prevRaw = (s.previous ?? "").toString().trim();
  const prev =
    prevRaw && prevRaw !== "—" && prevRaw.toLowerCase() !== "na"
      ? ` (prev ${prevRaw})`
      : "";
  return `Set ${index + 1}: ${w}×${r}${prev}`;
};

/** Superset color helpers: deterministic pastel based on ID */
const supersetColorsFromId = (id: string, darkMode?: Boolean) => {
  if (!id) {
    return {
      borderColor: "",
      backgroundColor: "",
    };
  }

  const normalized = id.trim();

  // Explicit, well-separated hues for common short IDs
  const presetHues: Record<string, number> = {
    "1": 0,
    "2": 130,
    "3": 260,
    a: 30,
    b: 160,
    c: 280,
    A: 30,
    B: 160,
    C: 280,
  };

  let hue: number;
  if (presetHues[normalized] !== undefined) {
    hue = presetHues[normalized];
  } else {
    let hash = 0;
    for (let i = 0; i < normalized.length; i++) {
      hash = normalized.charCodeAt(i) + ((hash << 5) - hash);
    }
    hue = Math.abs(hash) % 360;
  }

  const saturation = 65;
  const isDark = !!darkMode;
  const borderLightness = isDark ? 55 : 40;
  const bgLightness = isDark ? 15 : 94;

  return {
    borderColor: `hsl(${hue} ${saturation}% ${borderLightness}%)`,
    backgroundColor: `hsl(${hue} ${saturation}% ${bgLightness}%)`,
  };
};

/* ---------- Component ---------- */
const TemplateEditorDrawer: React.FC<TemplateEditorDrawerProps> = ({
  open,
  anchor = "right",
  draft: draftProp,
  exercises,
  onSave,
  onDelete,
  onClose,
  onChange,
  darkMode,
  titleOverride,
  filterExerciseOptions,
  availableTags,
}) => {
  const [draft, setDraft] = React.useState<TemplateType | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [dirty, setDirty] = React.useState(false);
  const [exerciseDialogIndex, setExerciseDialogIndex] = React.useState<
    number | null
  >(null);
  const [setsDialogIndex, setSetsDialogIndex] = React.useState<number | null>(
    null
  );
  const [snack, setSnack] = React.useState<{
    open: boolean;
    msg: string;
    severity: "success" | "error";
  }>({ open: false, msg: "", severity: "success" });

  const drawerBg = darkMode ? "#0f0f0f" : "#fff";
  const drawerBorder = darkMode
    ? "1px solid rgba(255,255,255,0.07)"
    : "1px solid rgba(0,0,0,0.06)";
  const drawerTextColor = darkMode ? "#f5f5f5" : "#000";
  const drawerSubTextColor = darkMode
    ? "rgba(255,255,255,0.7)"
    : "rgba(0,0,0,0.7)";
  const drawerFaintBg = darkMode
    ? "rgba(255,255,255,0.02)"
    : "rgba(0,0,0,0.02)";
  const drawerFaintBgHover = darkMode
    ? "rgba(255,255,255,0.04)"
    : "rgba(0,0,0,0.04)";

  const lastInitTemplateIdRef = React.useRef<string | null>(null);
  const wasOpenRef = React.useRef<boolean>(false);

  React.useEffect(() => {
    const defaultNew: TemplateType = {
      id: uid(),
      title: "New Template",
      exercises: [],
      difficulty: 3,
      dateLastUsed: "",
    } as TemplateType;

    const incoming = draftProp ?? defaultNew;
    const incomingId = incoming.id;

    const justOpened = open && !wasOpenRef.current;
    wasOpenRef.current = open;
    const idChanged = lastInitTemplateIdRef.current !== incomingId;

    if (open && (justOpened || idChanged || draft === null)) {
      const cloned = cloneTemplate(incoming);
      setDraft(cloned);
      setDirty(false);
      setExerciseDialogIndex(null);
      setSetsDialogIndex(null);
      lastInitTemplateIdRef.current = incomingId;
      return;
    }

    if (open && !idChanged && draftProp) {
      setDraft(cloneTemplate(draftProp));
    }
  }, [open, draftProp]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (open && dirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [open, dirty]);

  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!open) return;
      const metaOrCtrl = e.metaKey || e.ctrlKey;
      if (metaOrCtrl && e.key.toLowerCase() === "s") {
        e.preventDefault();
        if (!saving && draft?.title?.trim()) handleSave();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, saving, draft?.title]);

  const pushDirty = (
    next: TemplateType | ((d: TemplateType) => TemplateType)
  ) =>
    setDraft((d) => {
      const result = typeof next === "function" ? (next as any)(d!) : next;
      setDirty(true);
      onChange?.(result);
      return result;
    });

  const setField = <K extends keyof TemplateType>(k: K, v: TemplateType[K]) =>
    pushDirty({ ...(draft as TemplateType), [k]: v });

  const addExercise = () => {
    const nextIndex = draft?.exercises?.length ?? 0;
    pushDirty((d) => ({
      ...d,
      exercises: [
        ...d.exercises,
        {
          exercise: { id: null, name: "", type: null, muscleGroup: null },
          sets: [emptySet()],
          restTimer: { valueInSeconds: 60, unit: "lb" },
        },
      ],
    }));
    setExerciseDialogIndex(nextIndex);
  };

  const removeExerciseAt = (idx: number) =>
    setDraft((d) => {
      if (!d) return d;
      const newExercises = d.exercises.filter((_, i) => i !== idx);

      setExerciseDialogIndex((prev) => {
        if (prev == null) return null;
        if (prev === idx) return null;
        if (prev > idx) return prev - 1;
        return prev;
      });

      setSetsDialogIndex((prev) => {
        if (prev == null) return null;
        if (prev === idx) return null;
        if (prev > idx) return prev - 1;
        return prev;
      });

      const next: TemplateType = { ...d, exercises: newExercises };
      setDirty(true);
      onChange?.(next);
      return next;
    });

  const copyExerciseAt = (idx: number) =>
    setDraft((d) => {
      if (!d) return d;
      const item = d.exercises[idx];
      const duplicated: ExerciseTemplate = {
        ...item,
        sets: item.sets.map((s) => ({ ...s, id: uid() })),
      };
      const newExercises = [
        ...d.exercises.slice(0, idx + 1),
        duplicated,
        ...d.exercises.slice(idx + 1),
      ];
      const next: TemplateType = { ...d, exercises: newExercises };
      setDirty(true);
      onChange?.(next);
      return next;
    });

  const setExercise = (idx: number, ex: Partial<Exercise>) =>
    pushDirty((d) => ({
      ...d,
      exercises: d.exercises.map((et, i) =>
        i === idx ? { ...et, exercise: { ...et.exercise, ...ex } } : et
      ),
    }));

  const setRest = (idx: number, rest: Partial<ExerciseTemplate["restTimer"]>) =>
    pushDirty((d) => ({
      ...d,
      exercises: d.exercises.map((et, i) =>
        i === idx ? { ...et, restTimer: { ...et.restTimer, ...rest } } : et
      ),
    }));

  const addSetAt = (idx: number) =>
    pushDirty((d) => ({
      ...d,
      exercises: d.exercises.map((et, i) =>
        i === idx ? { ...et, sets: [...et.sets, emptySet()] } : et
      ),
    }));

  const removeSetAt = (idx: number, setId: string | undefined) =>
    pushDirty((d) => ({
      ...d,
      exercises: d.exercises.map((et, i) =>
        i === idx ? { ...et, sets: et.sets.filter((s) => s.id !== setId) } : et
      ),
    }));

  const setSetField = (
    idx: number,
    setId: string | undefined,
    part: "current.weight" | "current.reps" | "previous",
    value: number | string
  ) =>
    pushDirty((d) => ({
      ...d,
      exercises: d.exercises.map((et, i) => {
        if (i !== idx) return et;
        return {
          ...et,
          sets: et.sets.map((s) => {
            if (s.id !== setId) return s;
            if (part === "previous") return { ...s, previous: String(value) };
            if (part === "current.weight")
              return { ...s, current: { ...s.current, weight: Number(value) } };
            if (part === "current.reps")
              return { ...s, current: { ...s.current, reps: Number(value) } };
            return s;
          }),
        };
      }),
    }));

  // per-exercise supersets (0 or 1 tag)
  const setExerciseSuperSetTag = (idx: number, tag: string | null) =>
    pushDirty((d) => ({
      ...d,
      exercises: d.exercises.map((et, i) =>
        i === idx
          ? {
            ...et,
            superSetTags: tag && tag.trim() ? [tag.trim()] : [],
          }
          : et
      ),
    }));

  const availableSuperSetTags = React.useMemo(() => {
    if (!draft) return [] as string[];
    const set = new Set<string>();
    (draft.exercises as any[]).forEach((et) => {
      (et.superSetTags || []).forEach((t: string) => {
        const trimmed = (t || "").trim();
        if (trimmed) set.add(trimmed);
      });
    });
    return Array.from(set);
  }, [draft]);

  const handleSave = async () => {
    if (!draft?.title?.trim()) return;
    setSaving(true);
    try {
      // Normalize supersets to at most 1 tag
      const normalizedExercises = (draft.exercises || []).map((et: any) => {
        if (!Array.isArray(et.superSetTags)) return et;
        const first = (et.superSetTags[0] || "").toString().trim();
        return {
          ...et,
          superSetTags: first ? [first] : [],
        };
      });

      const finalT: TemplateType = {
        ...draft,
        title: draft.title.trim(),
        exercises: normalizedExercises,
      };

      await onSave(finalT);
      setDirty(false);
      setSnack({ open: true, msg: "Template saved", severity: "success" });
      onClose();
    } catch (e) {
      console.error(e);
      setSnack({
        open: true,
        msg: "Failed to save template",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!draft?.id || !onDelete) return;
    setSaving(true);
    try {
      await onDelete(draft.id);
      setDirty(false);
      setSnack({ open: true, msg: "Template deleted", severity: "success" });
      onClose();
    } catch (e) {
      console.error(e);
      setSnack({
        open: true,
        msg: "Failed to delete template",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const requestClose = () => {
    if (saving) return;
    if (dirty) {
      const ok = window.confirm("Discard unsaved changes?");
      if (!ok) return;
    }
    onClose();
  };

  const labelColor = {
    sx: {
      color: `${drawerTextColor} !important`,
      fontSize: "0.75rem",
      fontWeight: 500,
    },
  };

  const headerTitle =
    titleOverride ?? (draft?.title?.trim() ? draft.title : "Template");

  const { totalExercises, totalSets } = React.useMemo(
    () =>
      draft ? summarizeTemplate(draft) : { totalExercises: 0, totalSets: 0 },
    [draft]
  );

  const defaultFilter = React.useCallback(
    (options: Exercise[], state: { inputValue: string }) => {
      const q = (state.inputValue || "").toLowerCase().trim();
      if (!q) {
        return [...options].sort((a, b) =>
          (a?.name || "").localeCompare(b?.name || "", undefined, {
            sensitivity: "base",
            numeric: true,
          })
        );
      }
      return options
        .filter((e) => (e.name || "").toLowerCase().includes(q))
        .sort((a, b) =>
          (a?.name || "").localeCompare(b?.name || "", undefined, {
            sensitivity: "base",
            numeric: true,
          })
        );
    },
    []
  );

  const NumberBadge: React.FC<{ index: number }> = ({ index }) => (
    <Box
      sx={{
        width: 28,
        height: 28,
        minWidth: 28,
        borderRadius: "9999px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 700,
        fontSize: "0.7rem",
        lineHeight: 1.2,
        backgroundColor: darkMode ? "#000" : "#fff",
        color: darkMode ? "#fff" : "#000",
        border: darkMode
          ? "1px solid rgba(255,255,255,0.4)"
          : "1px solid rgba(0,0,0,0.15)",
        boxShadow: darkMode
          ? "inset 0 0 4px rgba(0,0,0,.6)"
          : "inset 0 0 4px rgba(0,0,0,.12)",
      }}
    >
      {index + 1}
    </Box>
  );

  if (!draft) return null;

  const editingIndex =
    exerciseDialogIndex != null &&
      exerciseDialogIndex >= 0 &&
      exerciseDialogIndex < draft.exercises.length
      ? exerciseDialogIndex
      : null;
  const setsIndex =
    setsDialogIndex != null &&
      setsDialogIndex >= 0 &&
      setsDialogIndex < draft.exercises.length
      ? setsDialogIndex
      : null;

  const editingExercise =
    editingIndex != null ? (draft.exercises[editingIndex] as any) : null;
  const setsExercise =
    setsIndex != null ? (draft.exercises[setsIndex] as any) : null;

  return (
    <>
      <Drawer
        anchor={anchor}
        open={open}
        onClose={requestClose}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 500, md: 1080, lg: 1080 },
            backgroundColor: drawerBg,
            color: drawerTextColor,
            borderLeft: drawerBorder,
            display: "flex",
            flexDirection: "column",
          },
        }}
        ModalProps={{
          keepMounted: true,
          disableScrollLock: true,
        }}
      >
        {/* HEADER */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: 2,
            py: 1.5,
            borderBottom: drawerBorder,
            gap: 1,
            color: drawerTextColor,
          }}
        >
          <Typography
            sx={{
              flex: 1,
              fontWeight: 600,
              fontSize: "1rem",
              lineHeight: 1.2,
              pr: 1,
              color: drawerTextColor,
            }}
          >
            {headerTitle}
            {dirty && (
              <Typography
                component="span"
                sx={{
                  ml: 1,
                  fontSize: "0.7rem",
                  fontWeight: 400,
                  lineHeight: 1.2,
                  color: drawerSubTextColor,
                }}
              >
                • unsaved
              </Typography>
            )}
          </Typography>

          <Button
            onClick={handleSave}
            variant="contained"
            size="small"
            disabled={!draft.title?.trim() || saving}
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              fontWeight: 600,
              fontSize: "0.8rem",
              lineHeight: 1.3,
              px: 1.5,
              py: 0.75,
              backgroundColor: darkMode ? "rgba(18,115,81,0.7)" : "#127351",
              "&:hover": {
                backgroundColor: darkMode ? "#5A8A50" : "#45A049",
              },
            }}
          >
            {saving ? "Saving..." : "Save"}
          </Button>

          <IconButton
            size="small"
            onClick={requestClose}
            sx={{ color: drawerTextColor }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {saving && (
          <LinearProgress
            sx={{
              backgroundColor: darkMode
                ? "rgba(255,255,255,0.07)"
                : "rgba(0,0,0,0.06)",
            }}
          />
        )}

        {/* BODY */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: 2,
            py: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
            color: drawerTextColor,
          }}
        >
          {/* DETAILS */}
          <Box>
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: 0.4,
                mb: 1,
                color: drawerSubTextColor,
              }}
            >
              Details
            </Typography>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              flexWrap="wrap"
              alignItems="flex-start"
            >
              <TextField
                label="Template Title"
                value={draft.title}
                onChange={(e) => setField("title", e.target.value)}
                size="small"
                InputLabelProps={labelColor}
                inputProps={{
                  style: {
                    fontSize: "0.9rem",
                    fontWeight: 600,
                    color: drawerTextColor,
                  },
                }}
                sx={{
                  minWidth: 220,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    color: drawerTextColor,
                    "& fieldset": {
                      borderColor: darkMode
                        ? "rgba(255,255,255,0.3)"
                        : "rgba(0,0,0,0.3)",
                    },
                    "&:hover fieldset": {
                      borderColor: darkMode
                        ? "rgba(255,255,255,0.6)"
                        : "rgba(0,0,0,0.6)",
                    },
                  },
                }}
                disabled={saving}
              />

              <TextField
                label="Difficulty"
                select
                value={draft.difficulty ?? 3}
                onChange={(e) => setField("difficulty", Number(e.target.value))}
                size="small"
                InputLabelProps={labelColor}
                inputProps={{
                  style: {
                    fontSize: "0.8rem",
                    fontWeight: 500,
                    color: drawerTextColor,
                  },
                }}
                sx={{
                  width: 120,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    color: drawerTextColor,
                    "& fieldset": {
                      borderColor: darkMode
                        ? "rgba(255,255,255,0.3)"
                        : "rgba(0,0,0,0.3)",
                    },
                    "&:hover fieldset": {
                      borderColor: darkMode
                        ? "rgba(255,255,255,0.6)"
                        : "rgba(0,0,0,0.6)",
                    },
                  },
                }}
                disabled={saving}
              >
                {[1, 2, 3, 4, 5].map((n) => (
                  <MenuItem
                    key={n}
                    value={n}
                    sx={{
                      fontSize: "0.8rem",
                      fontWeight: 500,
                      color: drawerTextColor,
                      backgroundColor: drawerBg,
                      "&.MuiMenuItem-root.Mui-selected": {
                        backgroundColor: darkMode
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(0,0,0,0.08)",
                      },
                    }}
                  >
                    {n}
                  </MenuItem>
                ))}
              </TextField>

              <Box sx={{ flex: 1, minWidth: 220 }}>
                <Autocomplete
                  multiple
                  freeSolo
                  size="small"
                  options={availableTags ?? []}
                  value={((draft as any).tags as string[]) || []}
                  onChange={(_, value) =>
                    setField(
                      "tags" as keyof TemplateType,
                      (value as string[])
                        .map((v) => v.trim())
                        .filter((v) => v.length > 0) as any
                    )
                  }
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const tagProps = getTagProps({ index });
                      return (
                        <Chip
                          {...tagProps}
                          key={option + index}
                          label={option}
                          size="small"
                          sx={{
                            borderRadius: 999,
                            fontSize: "0.75rem",
                            "& .MuiChip-deleteIcon": {
                              opacity: 0.7,
                            },
                          }}
                        />
                      );
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Tags"
                      placeholder="Add or select tags"
                      size="small"
                      InputLabelProps={labelColor}
                      inputProps={{
                        ...params.inputProps,
                        style: {
                          fontSize: "0.8rem",
                          color: drawerTextColor,
                        },
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          color: drawerTextColor,
                          "& fieldset": {
                            borderColor: darkMode
                              ? "rgba(255,255,255,0.3)"
                              : "rgba(0,0,0,0.3)",
                          },
                          "&:hover fieldset": {
                            borderColor: darkMode
                              ? "rgba(255,255,255,0.6)"
                              : "rgba(0,0,0,0.6)",
                          },
                        },
                      }}
                    />
                  )}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      color: drawerTextColor,
                      "& fieldset": {
                        borderColor: darkMode
                          ? "rgba(255,255,255,0.3)"
                          : "rgba(0,0,0,0.3)",
                      },
                      "&:hover fieldset": {
                        borderColor: darkMode
                          ? "rgba(255,255,255,0.6)"
                          : "rgba(0,0,0,0.6)",
                      },
                    },
                  }}
                  disabled={saving}
                />
              </Box>
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
              <Chip
                size="small"
                icon={
                  <FitnessCenterIcon sx={{ fontSize: "0.9rem !important" }} />
                }
                label={`${totalExercises} exercises • ${totalSets} sets`}
                sx={{
                  "& .MuiChip-label": {
                    fontWeight: 600,
                    fontSize: "0.7rem",
                    color: drawerTextColor,
                  },
                  "& .MuiChip-icon": {
                    color: drawerTextColor,
                  },
                  backgroundColor: drawerFaintBg,
                  border: drawerBorder,
                }}
              />
            </Stack>
          </Box>

          <Divider
            sx={{
              my: 0,
              borderColor: drawerBorder.split(" ")[2],
            }}
          />

          {/* EXERCISES */}
          <Box>
            <Stack
              direction="row"
              alignItems="baseline"
              justifyContent="space-between"
              sx={{ mb: 1 }}
            >
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                  color: drawerSubTextColor,
                }}
              >
                Exercises
              </Typography>

              <Button
                variant="contained"
                size="small"
                onClick={addExercise}
                disabled={saving}
                sx={{
                  backgroundColor: darkMode ? "rgba(18,115,81,0.7)" : "#127351",
                  textTransform: "none",
                  borderRadius: "8px",
                  fontWeight: 600,
                  fontSize: "0.7rem",
                  lineHeight: 1.2,
                  px: 1,
                  py: 0.5,
                  "&:hover": {
                    backgroundColor: darkMode ? "#5A8A50" : "#45A049",
                  },
                }}
              >
                Add exercise
              </Button>
            </Stack>

            {/* COLLAPSED ONLY */}
            <Stack spacing={2}>
              {draft.exercises.map((et: any, idx: number) => {
                const superSetTags: string[] = et.superSetTags || [];
                const primarySuperSetId = superSetTags[0];
                const { borderColor, backgroundColor } = primarySuperSetId
                  ? supersetColorsFromId(primarySuperSetId, darkMode)
                  : { borderColor: "", backgroundColor: "" };

                const cardBorder = borderColor || drawerBorder;
                const cardBgCollapsed = backgroundColor || drawerFaintBg;

                const sets: SetInfo[] = et.sets || [];

                return (
                  <Paper
                    key={`exercise-${idx}`}
                    elevation={0}
                    sx={{
                      p: 1.25,
                      borderRadius: 2,
                      cursor: "pointer",
                      transition:
                        "background-color 120ms ease, box-shadow 120ms ease, border-color 120ms ease",
                      backgroundColor: cardBgCollapsed,
                      border: cardBorder,
                      color: drawerTextColor,
                      "&:hover": {
                        boxShadow: primarySuperSetId
                          ? "0 0 0 1px rgba(0,0,0,0.12)"
                          : "none",
                      },
                    }}
                    onClick={() => setExerciseDialogIndex(idx)}
                    aria-label={`Edit exercise ${idx + 1}`}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="flex-start"
                      justifyContent="space-between"
                    >
                      <Stack
                        direction="row"
                        spacing={1.25}
                        alignItems="flex-start"
                        sx={{ minWidth: 0, flex: 1 }}
                      >
                        <NumberBadge index={idx} />

                        <Stack
                          direction="column"
                          spacing={0.25}
                          sx={{ minWidth: 0, flex: 1 }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.8rem",
                              lineHeight: 1.3,
                              color: drawerTextColor,
                            }}
                          >
                            {et.exercise?.name?.trim() || `Exercise ${idx + 1}`}
                          </Typography>

                          <Stack spacing={0.15} sx={{ mt: 0.25 }}>
                            {sets.length > 0 ? (
                              sets.map((s, i) => (
                                <Typography
                                  key={s.id || i}
                                  sx={{
                                    fontSize: "0.75rem",
                                    lineHeight: 1.4,
                                    fontWeight: 400,
                                    wordBreak: "break-word",
                                    color: drawerSubTextColor,
                                  }}
                                  title={setSummaryText(s, i)}
                                >
                                  {setSummaryText(s, i)}
                                </Typography>
                              ))
                            ) : (
                              <Typography
                                sx={{
                                  fontSize: "0.75rem",
                                  lineHeight: 1.4,
                                  fontWeight: 400,
                                  wordBreak: "break-word",
                                  color: drawerSubTextColor,
                                }}
                              >
                                No sets
                              </Typography>
                            )}
                          </Stack>
                        </Stack>
                      </Stack>

                      <Stack
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        sx={{ flexShrink: 0 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Tooltip title="Duplicate exercise block">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => copyExerciseAt(idx)}
                              disabled={saving}
                              sx={{ color: drawerTextColor }}
                            >
                              <ContentCopyIcon
                                sx={{ fontSize: "1rem", opacity: 0.7 }}
                              />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Remove exercise">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => removeExerciseAt(idx)}
                              disabled={saving}
                              sx={{ color: drawerTextColor }}
                            >
                              <DeleteOutlineIcon
                                sx={{ fontSize: "1rem", opacity: 0.7 }}
                              />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          </Box>

          <Divider
            sx={{
              borderColor: drawerBorder.split(" ")[2],
            }}
          />

          {/* SAVE / DELETE */}
          <Box>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={!draft.title?.trim() || saving}
                sx={{
                  textTransform: "none",
                  borderRadius: "10px",
                  fontWeight: 600,
                  fontSize: "0.8rem",
                  lineHeight: 1.3,
                  px: 1.5,
                  py: 0.75,
                  backgroundColor: darkMode ? "rgba(18,115,81,0.7)" : "#127351",
                  "&:hover": {
                    backgroundColor: darkMode ? "#5A8A50" : "#45A049",
                  },
                }}
              >
                Save Template
              </Button>

              {onDelete && (
                <Button
                  color="error"
                  onClick={handleDelete}
                  disabled={saving}
                  sx={{
                    textTransform: "none",
                    borderRadius: "10px",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    lineHeight: 1.3,
                    px: 1.5,
                    py: 0.75,
                    color: "#F44336",
                    "&:hover": {
                      backgroundColor: darkMode
                        ? "rgba(255,255,255,0.06)"
                        : "rgba(0,0,0,0.04)",
                    },
                  }}
                >
                  Delete
                </Button>
              )}
            </Stack>
          </Box>
        </Box>
      </Drawer>

      {/* EXERCISE DIALOG (expanded editor) */}
      {editingExercise && editingIndex != null && (
        <Dialog
          open={exerciseDialogIndex !== null}
          onClose={() => setExerciseDialogIndex(null)}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle
            sx={{
              fontSize: "0.9rem",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 1,
              backgroundColor: drawerBg,
              color: drawerTextColor,
              borderBottom: drawerBorder,
            }}
          >
            <NumberBadge index={editingIndex} />
            <Typography
              component="span"
              sx={{ fontSize: "0.9rem", fontWeight: 600 }}
            >
              Edit Exercise
            </Typography>
          </DialogTitle>
          <DialogContent
            dividers
            sx={{
              backgroundColor: drawerBg,
              color: drawerTextColor,
            }}
          >
            {/* Exercise autocomplete + quick actions */}
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1}
              alignItems={{ xs: "flex-start", md: "center" }}
              sx={{ mb: 1 }}
            >
              <Autocomplete
                options={exercises}
                filterOptions={filterExerciseOptions ?? defaultFilter}
                getOptionLabel={(o) => o?.name || ""}
                isOptionEqualToValue={(a, b) =>
                  (a?.id ?? a?.name) === (b?.id ?? b?.name)
                }
                value={
                  editingExercise.exercise?.name
                    ? editingExercise.exercise
                    : null
                }
                onChange={(_, val) => {
                  if (!val) return;
                  setExercise(editingIndex, {
                    id: val.id ?? null,
                    name: val.name ?? "",
                    type: val.type ?? null,
                    muscleGroup: val.muscleGroup ?? null,
                    shortYouTubeDemonstration:
                      val.shortYouTubeDemonstration ?? "",
                    inDepthYouTubeExplanation:
                      val.inDepthYouTubeExplanation ?? "",
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={`Exercise ${editingIndex + 1}`}
                    fullWidth
                    size="small"
                    placeholder='Search… e.g. "incline press"'
                    InputLabelProps={labelColor}
                    inputProps={{
                      ...params.inputProps,
                      style: {
                        fontSize: "0.8rem",
                        fontWeight: 500,
                        color: drawerTextColor,
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        color: drawerTextColor,
                        "& fieldset": {
                          borderColor: darkMode
                            ? "rgba(255,255,255,0.3)"
                            : "rgba(0,0,0,0.3)",
                        },
                        "&:hover fieldset": {
                          borderColor: darkMode
                            ? "rgba(255,255,255,0.6)"
                            : "rgba(0,0,0,0.6)",
                        },
                      },
                    }}
                  />
                )}
                sx={{
                  flex: 1,
                  minWidth: 280,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    color: drawerTextColor,
                    "& fieldset": {
                      borderColor: darkMode
                        ? "rgba(255,255,255,0.3)"
                        : "rgba(0,0,0,0.3)",
                    },
                    "&:hover fieldset": {
                      borderColor: darkMode
                        ? "rgba(255,255,255,0.6)"
                        : "rgba(0,0,0,0.6)",
                    },
                  },
                  "& .MuiAutocomplete-paper": {
                    backgroundColor: drawerBg,
                    color: drawerTextColor,
                  },
                }}
                disabled={saving}
              />

              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{ flexShrink: 0 }}
              >
                <Tooltip title="Duplicate exercise block">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => copyExerciseAt(editingIndex)}
                      disabled={saving}
                      sx={{ color: drawerTextColor }}
                    >
                      <ContentCopyIcon
                        sx={{ fontSize: "1rem", opacity: 0.7 }}
                      />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Remove exercise">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => {
                        removeExerciseAt(editingIndex);
                        setExerciseDialogIndex(null);
                      }}
                      disabled={saving}
                      sx={{ color: drawerTextColor }}
                    >
                      <DeleteOutlineIcon
                        sx={{ fontSize: "1rem", opacity: 0.7 }}
                      />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>

            {/* Superset tag (single) */}
            <Box sx={{ mt: 4 }}>
              <Autocomplete
                freeSolo
                size="small"
                options={availableSuperSetTags}
                value={
                  (editingExercise.superSetTags &&
                    editingExercise.superSetTags[0]) ||
                  ""
                }
                onChange={(_, value) => {
                  const v =
                    typeof value === "string"
                      ? value
                      : (value as any)?.toString?.() ?? "";
                  const cleaned = v.trim();
                  setExerciseSuperSetTag(editingIndex, cleaned || null);
                }}
                onInputChange={(_, value, reason) => {
                  if (reason === "input") {
                    // keep local input; actual commit happens in onChange when option is chosen or enter is pressed
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Superset ID"
                    placeholder='e.g. "A", "B", "1"'
                    size="small"
                    InputLabelProps={labelColor}
                    inputProps={{
                      ...params.inputProps,
                      style: {
                        fontSize: "0.8rem",
                        color: drawerTextColor,
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        color: drawerTextColor,
                        "& fieldset": {
                          borderColor: darkMode
                            ? "rgba(255,255,255,0.3)"
                            : "rgba(0,0,0,0.3)",
                        },
                        "&:hover fieldset": {
                          borderColor: darkMode
                            ? "rgba(255,255,255,0.6)"
                            : "rgba(0,0,0,0.6)",
                        },
                      },
                    }}
                  />
                )}
                sx={{
                  maxWidth: 420,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    color: drawerTextColor,
                    "& fieldset": {
                      borderColor: darkMode
                        ? "rgba(255,255,255,0.3)"
                        : "rgba(0,0,0,0.3)",
                    },
                    "&:hover fieldset": {
                      borderColor: darkMode
                        ? "rgba(255,255,255,0.6)"
                        : "rgba(0,0,0,0.6)",
                    },
                  },
                }}
                disabled={saving}
              />
            </Box>

            {/* VIDEO URLS */}
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1}
              sx={{ my: 4 }}
            >
              <TextField
                label="Short Demo (YouTube URL)"
                value={editingExercise.exercise.shortYouTubeDemonstration ?? ""}
                onChange={(e) =>
                  setExercise(editingIndex, {
                    shortYouTubeDemonstration: e.target.value,
                  })
                }
                fullWidth
                size="small"
                InputLabelProps={labelColor}
                inputProps={{
                  style: {
                    fontSize: "0.8rem",
                    fontWeight: 400,
                    lineHeight: 1.4,
                    color: drawerTextColor,
                  },
                }}
                InputProps={{
                  endAdornment:
                    editingExercise.exercise.shortYouTubeDemonstration?.trim() ? (
                      <InputAdornment position="end">
                        <Tooltip title="Open video">
                          <IconButton
                            size="small"
                            onClick={() =>
                              openUrl(
                                editingExercise.exercise
                                  .shortYouTubeDemonstration
                              )
                            }
                            disabled={saving}
                            sx={{ color: drawerTextColor }}
                          >
                            <OpenInNewIcon
                              sx={{ fontSize: "1rem", opacity: 0.7 }}
                            />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ) : undefined,
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    color: drawerTextColor,
                    "& fieldset": {
                      borderColor: darkMode
                        ? "rgba(255,255,255,0.3)"
                        : "rgba(0,0,0,0.3)",
                    },
                    "&:hover fieldset": {
                      borderColor: darkMode
                        ? "rgba(255,255,255,0.6)"
                        : "rgba(0,0,0,0.6)",
                    },
                  },
                }}
                disabled={saving}
              />

              <TextField
                label="In-Depth Demo (YouTube URL)"
                value={editingExercise.exercise.inDepthYouTubeExplanation ?? ""}
                onChange={(e) =>
                  setExercise(editingIndex, {
                    inDepthYouTubeExplanation: e.target.value,
                  })
                }
                fullWidth
                size="small"
                InputLabelProps={labelColor}
                inputProps={{
                  style: {
                    fontSize: "0.8rem",
                    fontWeight: 400,
                    lineHeight: 1.4,
                    color: drawerTextColor,
                  },
                }}
                InputProps={{
                  endAdornment:
                    editingExercise.exercise.inDepthYouTubeExplanation?.trim() ? (
                      <InputAdornment position="end">
                        <Tooltip title="Open video">
                          <IconButton
                            size="small"
                            onClick={() =>
                              openUrl(
                                editingExercise.exercise
                                  .inDepthYouTubeExplanation
                              )
                            }
                            disabled={saving}
                            sx={{ color: drawerTextColor }}
                          >
                            <OpenInNewIcon
                              sx={{ fontSize: "1rem", opacity: 0.7 }}
                            />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ) : undefined,
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    color: drawerTextColor,
                    "& fieldset": {
                      borderColor: darkMode
                        ? "rgba(255,255,255,0.3)"
                        : "rgba(0,0,0,0.3)",
                    },
                    "&:hover fieldset": {
                      borderColor: darkMode
                        ? "rgba(255,255,255,0.6)"
                        : "rgba(0,0,0,0.6)",
                    },
                  },
                }}
                disabled={saving}
              />
            </Stack>

            {/* REST + SETS CTA */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              sx={{ mt: 1 }}
              alignItems="center"
            >
              <TextField
                label="Rest (s)"
                type="number"
                value={editingExercise.restTimer.valueInSeconds}
                onChange={(e) =>
                  setRest(editingIndex, {
                    valueInSeconds: Number(e.target.value || 0),
                  })
                }
                InputProps={{ inputProps: { min: 5, max: 600 } }}
                size="small"
                InputLabelProps={labelColor}
                inputProps={{
                  style: {
                    fontSize: "0.8rem",
                    fontWeight: 500,
                    color: drawerTextColor,
                  },
                }}
                sx={{
                  width: 120,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    color: drawerTextColor,
                    "& fieldset": {
                      borderColor: darkMode
                        ? "rgba(255,255,255,0.3)"
                        : "rgba(0,0,0,0.3)",
                    },
                    "&:hover fieldset": {
                      borderColor: darkMode
                        ? "rgba(255,255,255,0.6)"
                        : "rgba(0,0,0,0.6)",
                    },
                  },
                }}
                disabled={saving}
              />

              <TextField
                label="Unit"
                select
                value={editingExercise.restTimer.unit}
                onChange={(e) =>
                  setRest(editingIndex, {
                    unit: e.target.value as "lb" | "kg",
                  })
                }
                size="small"
                InputLabelProps={labelColor}
                inputProps={{
                  style: {
                    fontSize: "0.8rem",
                    fontWeight: 500,
                    color: drawerTextColor,
                  },
                }}
                sx={{
                  width: 100,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    color: drawerTextColor,
                    "& fieldset": {
                      borderColor: darkMode
                        ? "rgba(255,255,255,0.3)"
                        : "rgba(0,0,0,0.3)",
                    },
                    "&:hover fieldset": {
                      borderColor: darkMode
                        ? "rgba(255,255,255,0.6)"
                        : "rgba(0,0,0,0.6)",
                    },
                  },
                }}
                disabled={saving}
              >
                <MenuItem
                  value="lb"
                  sx={{
                    fontSize: "0.8rem",
                    fontWeight: 500,
                    color: drawerTextColor,
                    backgroundColor: drawerBg,
                    "&.MuiMenuItem-root.Mui-selected": {
                      backgroundColor: darkMode
                        ? "rgba(255,255,255,0.08)"
                        : "rgba(0,0,0,0.08)",
                    },
                  }}
                >
                  lb
                </MenuItem>
                <MenuItem
                  value="kg"
                  sx={{
                    fontSize: "0.8rem",
                    fontWeight: 500,
                    color: drawerTextColor,
                    backgroundColor: drawerBg,
                    "&.MuiMenuItem-root.Mui-selected": {
                      backgroundColor: darkMode
                        ? "rgba(255,255,255,0.08)"
                        : "rgba(0,0,0,0.08)",
                    },
                  }}
                >
                  kg
                </MenuItem>
              </TextField>
            </Stack>

            <Stack direction="row" spacing={1} sx={{ mt: 4 }}>
              <Button
                size="small"
                onClick={() => setSetsDialogIndex(editingIndex)}
                variant="outlined"
                disabled={saving}
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  fontWeight: 600,
                  fontSize: "0.7rem",
                  lineHeight: 1.2,
                  px: 1,
                  py: 0.5,
                  borderColor: darkMode
                    ? "rgba(255,255,255,0.2)"
                    : "rgba(0,0,0,0.2)",
                  color: drawerTextColor,
                  "&:hover": {
                    backgroundColor: drawerFaintBgHover,
                  },
                }}
              >
                Manage sets ({editingExercise.sets?.length ?? 0})
              </Button>
            </Stack>

            {/* TEXT VERSION OF SETS SUMMARY IN EXERCISE MODAL */}
            <Box sx={{ mt: 2 }}>
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                  color: drawerSubTextColor,
                  mb: 0.5,
                }}
              >
                Sets summary
              </Typography>
              <Stack spacing={0.25}>
                {editingExercise.sets && editingExercise.sets.length > 0 ? (
                  editingExercise.sets.map((s: SetInfo, i: number) => (
                    <Typography
                      key={s.id || i}
                      sx={{
                        fontSize: "0.8rem",
                        lineHeight: 1.4,
                        color: drawerTextColor,
                      }}
                    >
                      {setSummaryText(s, i)}
                    </Typography>
                  ))
                ) : (
                  <Typography
                    sx={{
                      fontSize: "0.8rem",
                      lineHeight: 1.4,
                      color: drawerSubTextColor,
                    }}
                  >
                    No sets configured yet.
                  </Typography>
                )}
              </Stack>
            </Box>
          </DialogContent>
          <DialogActions
            sx={{
              backgroundColor: drawerBg,
              borderTop: drawerBorder,
            }}
          >
            <Button
              onClick={() => setExerciseDialogIndex(null)}
              size="small"
              sx={{
                textTransform: "none",
                fontSize: "0.8rem",
                color: drawerTextColor,
                fontWeight: 600,
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* SETS DIALOG */}
      {setsExercise && setsIndex != null && (
        <Dialog
          open={setsDialogIndex !== null}
          onClose={() => setSetsDialogIndex(null)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle
            sx={{
              fontSize: "0.9rem",
              fontWeight: 600,
              backgroundColor: drawerBg,
              color: drawerTextColor,
              borderBottom: drawerBorder,
            }}
          >
            Manage Sets — Exercise {setsIndex + 1}
          </DialogTitle>
          <DialogContent
            dividers
            sx={{
              backgroundColor: drawerBg,
              color: drawerTextColor,
            }}
          >
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2,
                backgroundColor: drawerFaintBg,
                border: drawerBorder,
                color: drawerTextColor,
              }}
            >
              <Stack spacing={0}>
                {setsExercise.sets.map((s: any, i: number) => (
                  <Stack
                    key={s.id || i}
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    alignItems={{ xs: "stretch", sm: "center" }}
                    sx={{
                      px: 1,
                      py: 1,
                      "&:not(:last-of-type)": {
                        borderBottom: drawerBorder,
                      },
                    }}
                  >
                    <Chip
                      size="small"
                      label={`Set ${i + 1}`}
                      sx={{
                        "& .MuiChip-label": {
                          fontWeight: 600,
                          fontSize: "0.7rem",
                          color: drawerTextColor,
                        },
                        backgroundColor: darkMode
                          ? "rgba(255,255,255,0.06)"
                          : "rgba(0,0,0,0.04)",
                        border: drawerBorder,
                        color: drawerTextColor,
                      }}
                    />

                    <TextField
                      label="Previous"
                      value={s.previous}
                      onChange={(e) =>
                        setSetField(setsIndex, s.id, "previous", e.target.value)
                      }
                      size="small"
                      InputLabelProps={labelColor}
                      inputProps={{
                        style: {
                          fontSize: "0.8rem",
                          fontWeight: 400,
                          color: drawerTextColor,
                        },
                      }}
                      sx={{
                        minWidth: 140,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          color: drawerTextColor,
                          "& fieldset": {
                            borderColor: darkMode
                              ? "rgba(255,255,255,0.3)"
                              : "rgba(0,0,0,0.3)",
                          },
                          "&:hover fieldset": {
                            borderColor: darkMode
                              ? "rgba(255,255,255,0.6)"
                              : "rgba(0,0,0,0.6)",
                          },
                        },
                      }}
                      disabled={saving}
                    />

                    <TextField
                      label="Weight"
                      type="number"
                      value={s.current.weight}
                      onChange={(e) =>
                        setSetField(
                          setsIndex,
                          s.id,
                          "current.weight",
                          Number(e.target.value || 0)
                        )
                      }
                      size="small"
                      InputLabelProps={labelColor}
                      inputProps={{
                        style: {
                          fontSize: "0.8rem",
                          fontWeight: 500,
                          color: drawerTextColor,
                        },
                      }}
                      sx={{
                        width: 120,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          color: drawerTextColor,
                          "& fieldset": {
                            borderColor: darkMode
                              ? "rgba(255,255,255,0.3)"
                              : "rgba(0,0,0,0.3)",
                          },
                          "&:hover fieldset": {
                            borderColor: darkMode
                              ? "rgba(255,255,255,0.6)"
                              : "rgba(0,0,0,0.6)",
                          },
                        },
                      }}
                      disabled={saving}
                    />

                    <TextField
                      label="Reps"
                      type="number"
                      value={s.current.reps}
                      onChange={(e) =>
                        setSetField(
                          setsIndex,
                          s.id,
                          "current.reps",
                          Number(e.target.value || 0)
                        )
                      }
                      size="small"
                      InputLabelProps={labelColor}
                      inputProps={{
                        style: {
                          fontSize: "0.8rem",
                          fontWeight: 500,
                          color: drawerTextColor,
                        },
                      }}
                      sx={{
                        width: 120,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          color: drawerTextColor,
                          "& fieldset": {
                            borderColor: darkMode
                              ? "rgba(255,255,255,0.3)"
                              : "rgba(0,0,0,0.3)",
                          },
                          "&:hover fieldset": {
                            borderColor: darkMode
                              ? "rgba(255,255,255,0.6)"
                              : "rgba(0,0,0,0.6)",
                          },
                        },
                      }}
                      disabled={saving}
                    />

                    <Box sx={{ flex: 1 }} />

                    <Tooltip title="Remove set">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => removeSetAt(setsIndex, s.id)}
                          disabled={saving}
                          sx={{ color: drawerTextColor }}
                        >
                          <DeleteOutlineIcon
                            sx={{ fontSize: "1rem", opacity: 0.7 }}
                          />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                ))}
              </Stack>
            </Paper>

            <Box sx={{ mt: 1 }}>
              <Button
                size="small"
                onClick={() => addSetAt(setsIndex)}
                variant="outlined"
                disabled={saving}
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  fontWeight: 600,
                  fontSize: "0.7rem",
                  lineHeight: 1.2,
                  px: 1,
                  py: 0.5,
                  borderColor: darkMode
                    ? "rgba(255,255,255,0.2)"
                    : "rgba(0,0,0,0.2)",
                  color: drawerTextColor,
                  "&:hover": {
                    backgroundColor: drawerFaintBgHover,
                  },
                }}
              >
                Add set
              </Button>
            </Box>
          </DialogContent>
          <DialogActions
            sx={{
              backgroundColor: drawerBg,
              borderTop: drawerBorder,
            }}
          >
            <Button
              onClick={() => setSetsDialogIndex(null)}
              size="small"
              sx={{
                textTransform: "none",
                fontSize: "0.8rem",
                color: drawerTextColor,
                fontWeight: 600,
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* SNACKBAR */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default TemplateEditorDrawer;
